// UI-трекинг кабинета — «куда люди кликают», в свою БД (таблица ui_events).
//
// Зачем не Метрика: цель Метрики отвечает «сколько раз нажали», но не джойнится с
// материалом, шортсом и балансом. Продуктовые вопросы почти всегда про «что было
// дальше»: нажал → заказал ли HD, вернулся ли завтра. Это только свой JOIN.
//
// Контракт:
//  - Всё в try/catch: аналитика никогда не роняет и не тормозит продукт.
//  - Пачкой, не по клику: очередь сливается раз в FLUSH_MS и на уходе со страницы.
//  - Имя события — константа из EVENTS ниже, а не строка по месту. Бэк отбрасывает
//    всё, что не проходит whitelist областей (back/lib/ui-events-helpers.php).
//
// Канон: vid2text/docs/analytics/UI_EVENT_TRACKING.md

import { API_URL } from '@/config.js';
import axiosConfig from '@/lib/axiosConfig';

const LS_ANON = 'ui_anon_id';
const SS_SESSION = 'ui_session_id';
const FLUSH_MS = 5000;
const MAX_QUEUE = 20;

// Схема имени: <область>.<объект>.<действие>. Область обязана быть в whitelist бэка.
// ⛔ Не заводить событие «на всякий случай»: строка, на которую не сформулирован вопрос,
// это не инсайт, а шум в GROUP BY.
export const EVENTS = {
  // Окно шортса — главный экран ClipCut, где решается «забрать или бросить».
  SHORT_TAB_OPEN: 'short.tab.open',            // target = имя вкладки (hook/flow/article/…)
  SHORT_ARTICLE_COPY: 'short.article.copy',    // target = top|bottom (какая из двух кнопок)
  SHORT_DOWNLOAD_HD: 'short.download.click',   // «Получить видео» = момент истины
  SHORT_SETTINGS_TOGGLE: 'short.settings.toggle',
  SHORT_SETTING_CHANGE: 'short.setting.change', // target = имя настройки (subtitle_position, color…)
  SHORT_REGENERATE: 'short.regenerate.click',
  SHORT_VIDEO_PLAY: 'short.video.play',
  SHORT_SUBTITLES_OPEN: 'short.subtitles.open',
  SHORT_SHARE: 'short.share.click',
  // Список материалов — навигация и поиск нужного.
  MATERIALS_CARD_OPEN: 'materials.card.open',  // value_num = позиция в списке
  MATERIALS_FILTER: 'materials.filter.apply',
  MATERIALS_SEARCH: 'materials.search.submit',
  // Материал целиком.
  MATERIAL_TAB_OPEN: 'material.tab.open',
  MATERIAL_DOWNLOAD: 'material.download.click',
  ARTICLE_GENERATE: 'article.generate.click',
  ARTICLE_COPY: 'article.copy.click',
  // Общее.
  UPLOAD_SUBMIT: 'upload.submit.click',
  NAV_CLICK: 'nav.link.click',                 // target = имя пункта
  AUTH_LOGIN_CLICK: 'auth.login.click',
  AUTH_REGISTER_CLICK: 'auth.register.click',
  BILLING_TOPUP_CLICK: 'billing.topup.click',
  // Стена оплаты показана (веб-дверь): target = точка (web_confirm_price|web_material_failed|web_extra_process).
  // Серверная пара — growth_events.paywall_shown (телеграм-дверь); сводит их clipcut_wall.py points.
  BILLING_PAYWALL_SHOWN: 'billing.paywall.shown',
  HELP_OPEN: 'help.open.click',
};

let queue = [];
let timer = null;
let listenersBound = false;

function uid() {
  try {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  } catch (e) { /* no-op */ }
  return 'x' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function anonId() {
  try {
    let v = localStorage.getItem(LS_ANON);
    if (!v) { v = uid(); localStorage.setItem(LS_ANON, v); }
    return v;
  } catch (e) { return ''; }
}

function sessionId() {
  try {
    let v = sessionStorage.getItem(SS_SESSION);
    if (!v) { v = uid(); sessionStorage.setItem(SS_SESSION, v); }
    return v;
  } catch (e) { return ''; }
}

function currentBot() {
  try {
    const host = window.location.hostname;
    // Порядок как в axiosConfig.js: clipcutbr раньше clipcut (подстрока).
    if (host.includes('meetscribe')) return 'meetscribe';
    if (host.includes('clipcutbr')) return 'clipcutbr';
    if (host.includes('clipcut')) return 'clipcut';
    if (host.includes('narezkavideo')) return 'narezkavideo';
    if (host.includes('ca.harikatha') || host.includes('chaitanyaacademy')) return 'chaitanyaacademy';
    if (host.includes('harikatha')) return 'harikatha';
    if (host.includes('edugrowth')) return 'edugrowth';
    if (host.includes('zvonalitik')) return 'zvonalitik';
    return 'vid2text';
  } catch (e) { return ''; }
}

function payload() {
  return { chatbot_name: currentBot(), events: queue.splice(0, queue.length) };
}

// Слив на живой странице — через axios: он несёт заголовок авторизации, и только этот
// путь связывает anon_id с user_id. Beacon на выгрузке заголовков не умеет (там user_id=0).
function flush() {
  try {
    if (timer) { clearTimeout(timer); timer = null; }
    if (!queue.length) return;
    axiosConfig.post('?action=log_ui_events', payload()).catch(() => {});
  } catch (e) { /* аналитика не роняет продукт */ }
}

// Слив на уходе со страницы: обычный POST браузер здесь уже отменяет, sendBeacon — нет.
function flushBeacon() {
  try {
    if (!queue.length) return;
    const body = new Blob([JSON.stringify(payload())], { type: 'application/json' });
    if (navigator.sendBeacon) navigator.sendBeacon(API_URL + '?action=log_ui_events', body);
  } catch (e) { /* no-op */ }
}

function bindListeners() {
  if (listenersBound) return;
  listenersBound = true;
  // pagehide + visibilitychange: на мобильных unload не срабатывает вовсе,
  // вкладку просто уводят в фон — без этого терялась бы вся последняя сессия.
  window.addEventListener('pagehide', flushBeacon);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushBeacon();
  });
}

/**
 * Записать событие.
 * @param {string} event имя из EVENTS
 * @param {object} [opts] { target, page, queue_id, short_id, value_num }
 */
export function track(event, opts = {}) {
  try {
    if (!event || window.__UI_TRACK_OFF) return;
    bindListeners();
    queue.push({
      event,
      target: String(opts.target ?? '').slice(0, 64),
      page: String(opts.page ?? (window.location.pathname.split('/')[2] || '')).slice(0, 64),
      queue_id: parseInt(opts.queue_id, 10) || 0,
      short_id: parseInt(opts.short_id, 10) || 0,
      value_num: parseInt(opts.value_num, 10) || 0,
      anon_id: anonId(),
      session_id: sessionId(),
      is_mobile: window.innerWidth < 992 ? 1 : 0,
    });
    if (queue.length >= MAX_QUEUE) { flush(); return; }
    if (!timer) timer = setTimeout(flush, FLUSH_MS);
  } catch (e) { /* аналитика не роняет продукт */ }
}

// Директива v-track: <button v-track="'short.download.click'"> или
// v-track="{ event: EVENTS.SHORT_TAB_OPEN, target: tab.name, short_id: id }".
// Нужна, чтобы разметка кликов не тащила @click-обёртки в каждый компонент.
// Директива v-track-view: событие один раз за сессию вкладки, когда элемент появился на экране
// (v-if сработал). Для показов, а не кликов: стена оплаты, пустое состояние. Ключ дедупа —
// событие + target + queue_id, чтобы перерисовка списка не множила показ.
const seenViews = new Set();
export const trackViewDirective = {
  mounted(el, binding) {
    try {
      const v = binding.value;
      if (!v || typeof v !== 'object' || !v.event) return;
      const key = [v.event, v.target || '', v.queue_id || 0].join('|');
      if (seenViews.has(key)) return;
      seenViews.add(key);
      track(v.event, v);
    } catch (e) { /* аналитика не роняет продукт */ }
  },
};

export const trackDirective = {
  mounted(el, binding) {
    const handler = () => {
      const v = binding.value;
      if (!v) return;
      if (typeof v === 'string') track(v);
      else track(v.event, v);
    };
    el.__uiTrackHandler = handler;
    el.addEventListener('click', handler, { passive: true });
  },
  unmounted(el) {
    if (el.__uiTrackHandler) el.removeEventListener('click', el.__uiTrackHandler);
  },
};

export default { track, EVENTS, trackDirective, trackViewDirective };
